import { useState } from 'react';
import { Link } from 'react-router-dom';

const nodes = [
  {
    id: 'diagnosis',
    label: 'Systems diagnosis',
    sub: 'Clarity before the next build',
    color: 'amber',
    intro: 'Architecture review, system redesign, and strategic depth — for operators who need clarity before the next build.',
    bestFor: [
      'Founders with live products that need rearchitecting',
      'Operators whose systems stopped scaling',
      'Teams before a major technical decision',
      'Anyone who inherited a codebase they didn\'t write',
    ],
    deliverables: [
      'Full technical architecture audit',
      'Bottleneck & risk identification',
      'Redesign roadmap with clear trade-offs',
      'Strategic technical advice for next steps',
    ],
    outcomes: [
      'Clarity on what\'s actually broken',
      'A plan that reduces risk before you spend',
      'Practical guidance from an active builder, not a consultant',
    ],
    cta: 'Book a Diagnosis Call',
  },
  {
    id: 'automation',
    label: 'Automation pipelines & bots',
    sub: 'Runs without you',
    color: 'purple',
    intro: 'Trading bots for CEX and DEX, workflow automation, AI agents, and custom pipelines — built as client deliverables, not internal experiments.',
    bestFor: [
      'Operators who need live trading systems',
      'Startups replacing manual workflows with automation',
      'Protocols needing multi-agent coordination',
      'Founders who have the idea and need the build',
    ],
    deliverables: [
      'CEX & DEX trading bots (trend, momentum, sniper strategies)',
      'Multi-agent AI automation architectures',
      'n8n / API workflow pipelines',
      'Custom AI assistants & internal tooling',
    ],
    outcomes: [
      'Systems that run around the clock',
      'Operational costs drop as volume scales',
      'Manual operations shift to automated, scalable systems',
    ],
    cta: 'Discuss Your System',
  },
  {
    id: 'infra',
    label: 'Decentralized infrastructure',
    sub: 'DApps · contracts · chain',
    color: 'teal',
    intro: 'Full-stack decentralized application development — from architecture to deployment on Polygon, BSC, and TON.',
    bestFor: [
      'Web3 startups shipping their first DApp',
      'SaaS founders adding blockchain layers',
      'Fintech & crypto projects needing SIWE auth',
      'Teams moving from testnet to production',
    ],
    deliverables: [
      'Web3 DApps & dashboards',
      'Smart contracts (Solidity, EVM-compatible)',
      'SIWE authentication & wallet integration',
      'Testnet → mainnet deployment on Polygon, BSC, TON',
    ],
    outcomes: [
      'Scalable architecture from day one',
      'Clean UX over complex on-chain systems',
      'Delivered and deployed, not just documented',
    ],
    cta: 'Discuss Your DApp',
  },
  {
    id: 'growth',
    label: 'Web3 ecosystem growth',
    sub: 'Protocol · token · community',
    color: 'coral',
    intro: 'Ecosystem strategy, community activation, and growth architecture for Web3 protocols and token economies.',
    bestFor: [
      'Web3 protocols with stalled adoption',
      'Token projects needing community engines',
      'Early-stage chains building their first user base',
      'DeFi operators expanding to new markets',
    ],
    deliverables: [
      'Ecosystem strategy & growth architecture',
      'Referral systems & community mechanics',
      'Token utility design & incentive structures',
      'Partner integrations & market positioning',
    ],
    outcomes: [
      'Active community that compounds over time',
      'Retention built into the protocol mechanics',
      'Growth that doesn\'t require constant spend',
    ],
    cta: 'Talk Ecosystem',
  },
  {
    id: 'presence',
    label: 'Digital presence & CRM automation',
    sub: 'Web2 + Web3 clients',
    color: 'blue',
    intro: 'End-to-end digital presence for operators and enterprises. Web presence, CRM setup, lead pipeline automation, content scheduling.',
    bestFor: [
      'Operators and founders who need a professional web presence',
      'Web2 or Web3 businesses setting up CRM pipelines',
      'Teams automating outbound and content workflows',
      'Anyone who needs the layer that makes everything else visible',
    ],
    deliverables: [
      'Web presence design & development',
      'CRM setup & lead pipeline automation',
      'Content scheduling & social automation',
      'Analytics & reporting integrations',
    ],
    outcomes: [
      'A presence that matches the quality of your product',
      'Automated pipeline that runs without daily input',
      'Measurable lead flow from day one',
    ],
    cta: 'Get Started',
  },
];

const colorMap = {
  amber:  { bg: '#1f1508', border: '#854F0B', text: '#FAC775', sub: '#a36b2b', check: '#d4913a' },
  purple: { bg: '#0d0b1f', border: '#534AB7', text: '#AFA9EC', sub: '#7a74c9', check: '#9b8fdf' },
  teal:   { bg: '#041a12', border: '#0F6E56', text: '#5DCAA5', sub: '#2d9478', check: '#3db38c' },
  coral:  { bg: '#1a0b06', border: '#993C1D', text: '#F0997B', sub: '#b55e3e', check: '#d4785a' },
  blue:   { bg: '#040f1f', border: '#185FA5', text: '#85B7EB', sub: '#4d8ec4', check: '#5fa0d8' },
};

function NodeCard({ node, open, toggle, wide }) {
  const c = colorMap[node.color];
  const isOpen = open === node.id;

  return (
    <div style={{ flex: wide ? '1 1 100%' : '1 1 0', minWidth: 0 }}>
      {/* Header row — always visible */}
      <div
        onClick={() => toggle(node.id)}
        style={{
          background: c.bg,
          border: `1px solid ${isOpen ? c.text : c.border}`,
          borderRadius: isOpen ? '8px 8px 0 0' : 8,
          padding: '14px 20px',
          cursor: 'pointer',
          transition: 'border-color 0.2s',
          userSelect: 'none',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <div style={{ color: c.text, fontWeight: 600, fontSize: 14 }}>{node.label}</div>
          <div style={{ color: c.sub, fontSize: 12, marginTop: 4 }}>{node.sub}</div>
        </div>
        <div style={{
          color: c.sub,
          fontSize: 16,
          lineHeight: 1,
          transform: isOpen ? 'rotate(45deg)' : 'none',
          transition: 'transform 0.2s',
          flexShrink: 0,
          marginLeft: 12,
        }}>+</div>
      </div>

      {/* Expanded panel */}
      {isOpen && (
        <div style={{
          background: c.bg,
          border: `1px solid ${c.border}`,
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: '20px 22px 22px',
        }}>
          {/* Intro */}
          <p style={{ fontSize: 13, color: c.text, lineHeight: 1.75, marginBottom: 18, opacity: 0.85 }}>
            {node.intro}
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: wide ? 'repeat(3, 1fr)' : '1fr', gap: '1.2rem', marginBottom: 18 }}>
            {/* Best for */}
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: c.sub, marginBottom: 8, opacity: 0.7 }}>Best for</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {node.bestFor.map((item, i) => (
                  <li key={i} style={{ fontSize: 12, color: c.text, opacity: 0.7, lineHeight: 1.5, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                    <span style={{ color: c.check, flexShrink: 0, marginTop: 1 }}>·</span>{item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Deliverables */}
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: c.sub, marginBottom: 8, opacity: 0.7 }}>What we deliver</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {node.deliverables.map((item, i) => (
                  <li key={i} style={{ fontSize: 12, color: c.text, opacity: 0.7, lineHeight: 1.5, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                    <span style={{ color: c.check, flexShrink: 0, marginTop: 1 }}>→</span>{item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Outcomes */}
            <div>
              <p style={{ fontSize: 10, letterSpacing: '0.25em', textTransform: 'uppercase', color: c.sub, marginBottom: 8, opacity: 0.7 }}>Business outcome</p>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {node.outcomes.map((item, i) => (
                  <li key={i} style={{ fontSize: 12, color: c.text, opacity: 0.7, lineHeight: 1.5, display: 'flex', gap: 7, alignItems: 'flex-start' }}>
                    <span style={{ color: c.check, flexShrink: 0, marginTop: 1 }}>✔</span>{item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* CTA */}
          <Link
            to="/contact"
            style={{
              display: 'inline-block',
              padding: '7px 18px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              textDecoration: 'none',
              border: `1px solid ${c.border}`,
              color: c.text,
              background: 'transparent',
              transition: 'background 0.2s, border-color 0.2s',
              letterSpacing: '0.02em',
            }}
            onMouseOver={e => { e.currentTarget.style.background = `${c.bg}cc`; e.currentTarget.style.borderColor = c.text; }}
            onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = c.border; }}
          >
            {node.cta} →
          </Link>
        </div>
      )}
    </div>
  );
}

const ServicesArchitecture = () => {
  const [open, setOpen] = useState(null);
  const toggle = (id) => setOpen(open === id ? null : id);

  return (
    <div style={{ padding: '3rem 0 1rem', maxWidth: 900, margin: '0 auto' }}>
      <p style={{
        fontSize: '10px',
        letterSpacing: '0.35em',
        textTransform: 'uppercase',
        opacity: 0.35,
        marginBottom: '1.5rem',
        textAlign: 'center',
      }}>
        Service Map
      </p>

      {/* Diagnosis — top */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
        <div style={{ width: '60%' }}>
          <NodeCard node={nodes[0]} open={open} toggle={toggle} />
        </div>
      </div>

      {/* Connector */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.1)' }} />
      </div>

      {/* Three core services */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', marginBottom: '0.5rem' }}>
        {nodes.slice(1, 4).map(n => (
          <NodeCard key={n.id} node={n} open={open} toggle={toggle} />
        ))}
      </div>

      {/* Connector */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
        <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.1)' }} />
      </div>

      {/* Digital presence — full width */}
      <NodeCard node={nodes[4]} open={open} toggle={toggle} wide />
    </div>
  );
};

export default ServicesArchitecture;
